import { useCallback, useEffect, useState } from 'react';
import { type AudioDevice, DEFAULT_DEVICE_ID, deviceName, listAudioDevices } from './devices';

/** The result of the headset check, kept in this browser (it describes this computer's devices). */
export interface HeadsetSettings {
  inputDeviceId: string;
  inputLabel: string;
  /** '' to follow the system default output. */
  outputDeviceId: string;
  /** The device's label; for the system default, the label of the device that was default at check time. */
  outputLabel: string;
  /** Playback-to-recording delay, milliseconds. */
  latencyMs: number;
  latencySource: 'measured' | 'estimated';
  checkedAt: string;
}

const KEY = 'corocanto.headset.v1';
const EVENT = 'corocanto-headset';

export function loadHeadset(): HeadsetSettings | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as HeadsetSettings) : null;
  } catch {
    return null;
  }
}

export function saveHeadset(settings: HeadsetSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Storage unavailable: the check simply has to be repeated next time.
  }
  window.dispatchEvent(new Event(EVENT));
}

export function clearHeadset(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event(EVENT));
}

/**
 * Are the checked devices still the ones in use? A device that disappeared, or an id that now
 * means another device, invalidates the check. This includes the system default: if the check
 * was done on the default output (or input) and the default is now another device, for example
 * a Bluetooth headset, the delay measured before no longer applies.
 */
export function devicesStillMatch(settings: HeadsetSettings, inputs: readonly AudioDevice[], outputs: readonly AudioDevice[]): boolean {
  const same = (list: readonly AudioDevice[], id: string, label: string) => {
    const device = list.find((d) => d.deviceId === id);
    if (!device) return false;
    return !device.label || !label || device.label === label;
  };
  const inputOk = same(inputs, settings.inputDeviceId, settings.inputLabel);
  const outputId = settings.outputDeviceId || DEFAULT_DEVICE_ID;
  // Browsers without a "default" entry (Firefox) cannot tell which device is the default.
  const outputOk = outputId === DEFAULT_DEVICE_ID && !outputs.some((d) => d.deviceId === DEFAULT_DEVICE_ID)
    ? true
    : same(outputs, outputId, settings.outputLabel);
  return inputOk && outputOk;
}

export async function headsetStillValid(settings: HeadsetSettings): Promise<boolean> {
  try {
    const { inputs, outputs } = await listAudioDevices();
    return devicesStillMatch(settings, inputs, outputs);
  } catch {
    return false;
  }
}

/** The saved headset check, re-validated whenever devices change. */
export function useHeadset() {
  const [settings, setSettings] = useState<HeadsetSettings | null>(() => loadHeadset());
  const [valid, setValid] = useState<boolean>(false);

  const refresh = useCallback(async () => {
    const current = loadHeadset();
    setSettings(current);
    setValid(current ? await headsetStillValid(current) : false);
  }, []);

  useEffect(() => {
    void refresh();
    const onChange = () => void refresh();
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onChange);
    navigator.mediaDevices?.addEventListener?.('devicechange', onChange);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onChange);
      navigator.mediaDevices?.removeEventListener?.('devicechange', onChange);
    };
  }, [refresh]);

  return { settings: valid ? settings : null, refresh };
}

/** Names of the microphone and output the system currently uses by default (Chrome), kept up to date. */
export function useSystemDefaultDeviceNames(): { input: string | null; output: string | null } {
  const [names, setNames] = useState<{ input: string | null; output: string | null }>({ input: null, output: null });
  useEffect(() => {
    const name = (list: readonly AudioDevice[]) => {
      const d = list.find((x) => x.deviceId === DEFAULT_DEVICE_ID);
      return d?.label ? deviceName(d.label) : null;
    };
    const read = () =>
      void listAudioDevices()
        .then(({ inputs, outputs }) => setNames({ input: name(inputs), output: name(outputs) }))
        .catch(() => setNames({ input: null, output: null }));
    read();
    navigator.mediaDevices?.addEventListener?.('devicechange', read);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', read);
  }, []);
  return names;
}
